import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { TooltipContainer, TooltipBubblePortal } from "./styles";

type TooltipSide = "top" | "left";
type AlignAbove = "start" | "center";

interface TooltipProps {
    /** Conteúdo exibido ao passar o mouse */
    content: string;
    /** Se true, permite quebra de linha e largura máxima (ex.: nomes longos) */
    longContent?: boolean;
    /** Lado em que o tooltip aparece: "top" (acima) ou "left" (à esquerda, evita corte na tela) */
    side?: TooltipSide;
    /** Se true, centraliza o tooltip acima do elemento trigger (útil em mobile/responsivo) */
    centerAboveTrigger?: boolean;
    /** Com centerAboveTrigger: "start" = alinha ao início do trigger, "center" = centraliza (default) */
    alignAbove?: AlignAbove;
    /** Só mostra o tooltip quando há conteúdo */
    children: React.ReactNode;
}

const OFFSET = 8;

/**
 * Tooltip customizado do sistema (estilo tippy).
 * Renderizado em Portal para não ser cortado por overflow dos pais.
 */
export default function Tooltip({ content, longContent = false, side = "top", centerAboveTrigger = false, alignAbove = "center", children }: TooltipProps) {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const hasContent = (content || "").trim().length > 0;

    const BUBBLE_MAX_WIDTH = 320;
    const BUBBLE_PADDING = 16;

    useEffect(() => {
        if (!show || !hasContent || !triggerRef.current) {
            if (!show) setPosition(null);
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        const w = typeof window !== "undefined" ? window.innerWidth : 0;

        if (side === "left") {
            let left = rect.left;
            if (longContent) {
                const minLeft = BUBBLE_MAX_WIDTH + OFFSET + BUBBLE_PADDING;
                left = Math.max(minLeft, Math.min(left, w - BUBBLE_PADDING));
            }
            setPosition({
                top: rect.top + rect.height / 2,
                left,
            });
        } else {
            const alignStart = centerAboveTrigger && alignAbove === "start";
            let left = alignStart ? rect.left : rect.left + rect.width / 2;
            if (longContent && typeof window !== "undefined") {
                if (alignStart) {
                    left = Math.max(BUBBLE_PADDING, Math.min(left, w - BUBBLE_MAX_WIDTH - BUBBLE_PADDING));
                } else {
                    const half = BUBBLE_MAX_WIDTH / 2;
                    left = Math.max(half + BUBBLE_PADDING, Math.min(w - half - BUBBLE_PADDING, left));
                }
            }
            setPosition({
                top: rect.top,
                left,
            });
        }
    }, [show, hasContent, side, longContent, centerAboveTrigger, alignAbove]);

    /* No mobile/responsivo: ao scrollar, fecha o tooltip para não ficar fixo na tela */
    useEffect(() => {
        if (!show || !hasContent) return;
        const isMobile = () => typeof window !== "undefined" && window.innerWidth <= 768;
        const closeOnScroll = () => {
            if (isMobile()) setShow(false);
        };
        window.addEventListener("scroll", closeOnScroll, { passive: true });
        window.addEventListener("touchmove", closeOnScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", closeOnScroll);
            window.removeEventListener("touchmove", closeOnScroll);
        };
    }, [show, hasContent]);

    const alignStart = centerAboveTrigger && alignAbove === "start";
    const bubbleStyle = centerAboveTrigger || !longContent
        ? (() => {
              let left = position?.left ?? 0;
              if (longContent && !alignStart && typeof window !== "undefined") {
                  const half = BUBBLE_MAX_WIDTH / 2;
                  left = Math.max(half + BUBBLE_PADDING, Math.min(window.innerWidth - half - BUBBLE_PADDING, left));
              }
              return {
                  top: position ? position.top - OFFSET : 0,
                  left,
                  transform: alignStart ? "translate(0, -100%)" : "translate(-50%, -100%)",
              };
          })()
        : side === "left"
            ? {
                  top: position?.top ?? 0,
                  left: position?.left ?? 0,
                  transform: "translate(calc(-100% - 8px), -50%)",
              }
            : (() => {
                  let left = position?.left ?? 0;
                  if (longContent && typeof window !== "undefined") {
                      const half = BUBBLE_MAX_WIDTH / 2;
                      left = Math.max(half + BUBBLE_PADDING, Math.min(window.innerWidth - half - BUBBLE_PADDING, left));
                  }
                  return {
                      top: position ? position.top - OFFSET : 0,
                      left,
                      transform: "translate(-50%, -100%)",
                  };
              })();

    return (
        <>
            <TooltipContainer
                ref={triggerRef}
                onMouseEnter={() => hasContent && setShow(true)}
                onMouseLeave={() => setShow(false)}
            >
                {children}
            </TooltipContainer>
            {show &&
                hasContent &&
                position &&
                typeof document !== "undefined" &&
                createPortal(
                    <TooltipBubblePortal longContent={longContent} side={side} $centerAboveTrigger={centerAboveTrigger} $alignStart={alignStart} style={bubbleStyle}>
                        {content}
                    </TooltipBubblePortal>,
                    document.body
                )}
        </>
    );
}
